import { Scope } from '../types';
import { Logger } from '../lib/logger';
import {
  OidcConfigManager,
  SupportedCodeChallengeMethod,
  SupportedGrantType,
  SupportedResponseMode,
  SupportedResponseType,
} from '../oidc/oidc-config-manager';
import { AuthorizationError } from '../lib/authorization-error';
import { LoggerNames } from '../lib/constants';
import { RequestUtil } from '../request-util/request-util';

const CONTENT_TYPE = {
  JSON: 'application/json',
  FORM: 'application/x-www-form-urlencoded',
};

export type V2RouterConfig = {
  issuer: string;
  oidcManager: OidcConfigManager;
};

export type V2LoginRequest = {
  // OAUTH 2.0 params
  client_id: string;
  redirect_uri: string;
  scope: Scope;
  state: string;
  code_challenge: string;
  code_challenge_method: SupportedCodeChallengeMethod;
  response_type: SupportedResponseType;
  response_mode: SupportedResponseMode;
  prompt: 'display' | 'none';
  // OpenID Connect params
  nonce: string;
  // deprecated
  sessionToken?: string;
};

export type V2LogoutRequest = {
  id_token_hint: string;
  state: string;
  post_logout_redirect_uri: string;
  client_id: string;
};

export type V2TokenRequest<TGrantType extends SupportedGrantType> = TGrantType extends 'authorization_code'
  ? {
      client_id: string;
      redirect_uri: string;
      grant_type: 'authorization_code';
      code_verifier: string;
      code: string;
    }
  : TGrantType extends 'refresh_token'
    ? { client_id: string; redirect_uri: string; grant_type: 'refresh_token'; refresh_token: string }
    : never;

export type V2TokenResponse = {
  token_type: string;
  expires_in: number;
  access_token: string;
  refresh_token: string;
  id_token: string;
  scope: string;
};

export type V2MeResponse = {
  login: string;
  eventId: string;
  userId: string;
  createdAt: string;
  expiresAt: string;
  status: 'Active';
  clientId: string;
};

export type V2UserInfoRequest = {
  accessToken: string;
};

export type V2UserInfoResponse = {
  sub: string;
  login: string;
  given_name: string;
  family_name: string;
  zoneinfo: string;
  name: string;
  nickname: string;
  locale: string;
  email: string;
  email_verified: boolean;
};

export type V2CheckSessionRequest = {
  accessToken: string;
};

export class V2Router {
  private readonly requiredHeaders = {
    Accept: CONTENT_TYPE.JSON,
    // TODO: Enable version header when we have it whitelisted (header name may change)
    // 'X-Auth-SDK-Version': __MAP_SSO_SDK_VERSION__,
  };
  private readonly logger = Logger.createLogger(LoggerNames.ROUTER);
  private readonly oidcManager: OidcConfigManager;
  private readonly issuer: string;

  private clientHeaders: Record<string, string>;

  constructor({ oidcManager, issuer }: V2RouterConfig) {
    this.oidcManager = oidcManager;
    this.issuer = issuer;
    this.clientHeaders = {};
  }

  public setHeaders(headers: Record<string, string>) {
    this.clientHeaders = headers;
  }

  /**
   * Returns a URL the client can use to redirect to the login page.
   * Will have all the appropriate query parameters set for an OIDC/OAUth2
   * PKCE redirection flow.
   *
   * @param request
   */
  async login(request: V2LoginRequest): Promise<URL> {
    this.logger.debug('Starting login request');
    const authorizationUrl = await this.oidcManager.authorizationUrl();
    const loginUrl = new URL(authorizationUrl);
    const params = new URLSearchParams(request);
    loginUrl.search = params.toString();

    return loginUrl;
  }

  /**
   * Returns a URL the client can use to redirect to the logout page.
   * Will have all the appropriate query parameters set for an OIDC/OAUth2
   * RP-initiated logout flow.
   *
   * @param request
   */
  async logout(request: V2LogoutRequest): Promise<URL> {
    this.logger.debug('Starting logout request');
    const endSessionUrl = await this.oidcManager.endSessionUrl();
    const logoutUrl = new URL(endSessionUrl);
    const params = new URLSearchParams(request);
    logoutUrl.search = params.toString();

    return logoutUrl;
  }

  /**
   * Used to fetch an access token. Supports the following grant types:
   * - authorization_code
   * - refresh_token
   * See V2TokenRequest type for parameters required for each grant type.
   * @param request
   * @throws {NetworkError}
   */
  async token<TGrantType extends SupportedGrantType>(request: V2TokenRequest<TGrantType>): Promise<V2TokenResponse> {
    const tokenUrl = await this.oidcManager.tokenUrl();
    const params = new URLSearchParams(request);

    return this.exec<V2TokenResponse>(tokenUrl, {
      method: 'POST',
      body: params,
      headers: {
        'Content-Type': CONTENT_TYPE.FORM,
      },
    });
  }

  /**
   * Given a location, attempts to parse the authorization code and state from the location.
   * Will throw an error if the location does not contain both code and state query parameters.
   *
   * @param location
   * @throws { AuthorizationError }
   */
  parseRedirectUrl(location: string = window.location.href) {
    const url = new URL(location);
    const params = new URLSearchParams(url.search);
    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) {
      this.logger.error(`Could not parse authorization code from url`, { location });
      throw new AuthorizationError('MissingAuthorizationCode', 'Could not parse redirect parameters from url');
    }

    return { code, state };
  }

  /**
   * Checks the user has a valid session. This will return a 200 if the user has a valid session,
   * and a 401 if the user does not have a valid session.
   * @param accessToken
   */
  async checkSession({ accessToken }: V2CheckSessionRequest): Promise<boolean> {
    const issuer = await this.oidcManager.issuer();
    try {
      // if the request succeeds, they have a valid session
      await this.exec(
        `${issuer}/oauth2/v1/checkSession`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
        false,
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Returns the active session for a user.
   * Requires third-party cookies to be enabled
   * @throws {NetworkError}
   */
  async me(): Promise<V2MeResponse> {
    const issuer = await this.oidcManager.issuer();
    return this.exec<V2MeResponse>(`${issuer}/api/v1/sessions/me`, {
      method: 'GET',
      credentials: 'include',
    });
  }

  async deleteMe(): Promise<void> {
    const issuer = await this.oidcManager.issuer();
    return this.exec<void>(`${issuer}/api/v1/sessions/me`, { method: 'DELETE', credentials: 'include' }, false);
  }

  /**
   * Returns the user info from the SSO service.
   * @param accessToken
   * @throws {NetworkError}
   */
  async userInfo({ accessToken }: V2UserInfoRequest): Promise<V2UserInfoResponse> {
    return this.exec<V2UserInfoResponse>(await this.oidcManager.userInfoUrl(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  /**
   * Executes a fetch request with the given url and request options.
   * Utilizes RequestUtil to set credentials and attempt retries on network failure.
   * @param url The url to fetch
   * @param req  The request options. Compatible with the fetch API.
   * @param parse Whether to parse the response as JSON. Defaults to true.
   * @throws {NetworkError}
   */
  async exec<TResponse>(url: string, req: RequestInit, parse = true): Promise<TResponse> {
    return RequestUtil.request(
      url,
      { ...req, headers: { ...this.clientHeaders, ...this.requiredHeaders, ...req.headers } },
      parse,
    );
  }
}
